export type RoundingMode = "halfUp" | "halfDown" | "floor" | "ceil" | "truncate";

/**
 * Rounds a number to the specified decimal scale using the given rounding mode.
 *
 * @param value - The number to round (can be null)
 * @param decimalScale - Number of decimal places (undefined = no rounding)
 * @param mode - Rounding mode (default: "halfUp" = commercial rounding)
 * @returns The rounded number or null if input was null
 *
 * @example
 * roundNumber(2.345, 2, "halfUp")    // 2.35 (commercial rounding)
 * roundNumber(2.345, 2, "halfDown")  // 2.34
 * roundNumber(2.349, 2, "floor")     // 2.34
 * roundNumber(2.341, 2, "ceil")      // 2.35
 * roundNumber(2.349, 2, "truncate")  // 2.34
 */
export function roundNumber(
	value: number | null,
	decimalScale: number | undefined,
	mode: RoundingMode = "halfUp",
): number | null {
	if (value === null || decimalScale === undefined) {
		return value;
	}

	const multiplier = Math.pow(10, decimalScale);
	// Add a small epsilon to handle floating point precision issues
	// (e.g., 75.345 * 100 = 7534.4999999999995 instead of 7534.5)
	const epsilon = 1e-10;
	const shifted = value * multiplier;

	let rounded: number;

	switch (mode) {
		case "halfUp": {
			// Commercial rounding: round half away from zero
			// Add epsilon to compensate for floating point errors when value is exactly at 0.5
			const absShifted = Math.abs(shifted);
			rounded = Math.sign(shifted) * Math.round(absShifted + epsilon);
			break;
		}

		case "halfDown": {
			// Round half toward zero (only round up if strictly greater than 0.5)
			const absShifted = Math.abs(shifted);
			const fraction = absShifted - Math.floor(absShifted);
			// Use a small epsilon to handle floating point precision
			if (fraction > 0.5 + 1e-9) {
				rounded = Math.sign(shifted) * Math.ceil(absShifted);
			} else {
				rounded = Math.sign(shifted) * Math.floor(absShifted);
			}
			break;
		}

		case "floor":
			// Always round toward negative infinity
			rounded = Math.floor(shifted);
			break;

		case "ceil":
			// Always round toward positive infinity
			rounded = Math.ceil(shifted);
			break;

		case "truncate":
			// Always round toward zero (cut off decimals)
			rounded = Math.trunc(shifted);
			break;
	}

	return rounded / multiplier;
}

/**
 * Formats a number for display using Intl.NumberFormat.
 * This is useful for displaying values outside of input fields.
 *
 * @param value - The number to format
 * @param locale - The locale to use (e.g., "de", "en")
 * @param options - Additional Intl.NumberFormat options
 */
export function formatNumber(
	value: number | null,
	locale: string,
	options?: Intl.NumberFormatOptions,
): string {
	if (value === null) {
		return "";
	}

	return new Intl.NumberFormat(locale, options).format(value);
}
